/**
 * EmailTemplatesSection — zentrale E-Mail-Vorlagen-Verwaltung (Einstellungen).
 *
 * Der Händler hinterlegt pro E-Mail-Typ einen eigenen Begleittext. Dieser Text
 * wird sowohl bei MANUELL ausgelösten als auch bei AUTOMATISCH versendeten
 * E-Mails dieses Typs verwendet (Pflicht-/Sicherheitsblöcke werden serverseitig
 * unveränderbar angehängt). Speicherung über
 *   GET  /api/dealer/email-templates
 *   PUT  /api/dealer/email-templates/{key}  { text }
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Save, RotateCcw, ChevronDown, Eye, Send, X, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import BranchOverrideToggle from "./BranchOverrideToggle";

function PreviewModal({ t, text, onClose }) {
    const [data, setData] = useState(null);
    const [testing, setTesting] = useState(false);
    useEffect(() => {
        api.post(`/dealer/email-templates/${t.key}/preview`, { text })
            .then((r) => setData(r.data))
            .catch(() => setData({ subject: "—", html: "<p style='font-family:sans-serif;padding:16px'>Vorschau nicht verfügbar.</p>" }));
    }, [t.key, text]);

    const sendTest = async () => {
        setTesting(true);
        try {
            const r = await api.post(`/dealer/email-templates/${t.key}/test-mail`, { text });
            toast.success(r.data.mocked
                ? `Test-Mail an ${r.data.to} protokolliert (GEMOCKT — kein Resend-Key)`
                : `Test-Mail an ${r.data.to} gesendet`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test-Mail fehlgeschlagen");
        } finally { setTesting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="email-template-preview-modal">
            <div className="bg-white rounded-sm w-full max-w-xl mt-8 border border-gray-200" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200">
                    <Eye className="w-5 h-5 text-[#FFCC00]" />
                    <h3 className="font-display font-black text-base">Vorschau — {t.label}</h3>
                    <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700" data-testid="email-template-preview-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    {!data ? (
                        <div className="flex items-center gap-2 text-gray-500 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Vorschau wird gerendert…</div>
                    ) : (
                        <>
                            <div className="text-sm"><span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Betreff</span><div className="font-bold text-[#111827]" data-testid="email-template-preview-subject">{data.subject}</div></div>
                            <iframe title="E-Mail-Vorschau" srcDoc={data.html} className="w-full h-[420px] border border-gray-200 rounded-sm bg-white" data-testid="email-template-preview-frame" />
                            <p className="text-[11px] text-gray-500">Beispieldaten (Max Mustermann · BMW R 1250 GS). Pflicht-Blöcke werden im echten Versand fix angehängt.</p>
                            <Button type="button" onClick={sendTest} disabled={testing}
                                className="w-full h-10 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black font-bold"
                                data-testid="email-template-test-mail">
                                {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Test-Mail an mich senden
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function TemplateRow({ t, onSaved, initialOpen = false, autoSend, onToggleAutoSend }) {
    const [text, setText] = useState(t.text || "");
    const [open, setOpen] = useState(!!initialOpen);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(false);
    const [enabled, setEnabled] = useState(t.enabled !== false);
    const [aiBusy, setAiBusy] = useState(false);
    const dirty = (text || "") !== (t.text || "");

    const aiSuggest = async () => {
        setAiBusy(true);
        const mode = text.trim() ? "improve" : "suggest";
        try {
            const r = await api.post(`/dealer/email-templates/${t.key}/ai-suggest`, { text, mode });
            if (r.data?.text) {
                setText(r.data.text);
                toast.success(mode === "improve" ? "KI-Vorschlag eingefügt (überarbeitet)" : "KI-Vorschlag eingefügt");
            } else {
                toast.error("Keine KI-Antwort erhalten");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "KI-Vorschlag fehlgeschlagen");
        } finally { setAiBusy(false); }
    };

    const toggleEnabled = async (e) => {
        e.stopPropagation();
        const next = !enabled;
        setEnabled(next);
        try {
            await api.put(`/dealer/email-templates/${t.key}/enabled`, { enabled: next });
            toast.success(next ? "Automatischer Versand aktiviert" : "Automatischer Versand deaktiviert");
        } catch (err) {
            setEnabled(!next);
            toast.error(err?.response?.data?.detail || "Umschalten fehlgeschlagen");
        }
    };

    const save = async () => {
        setBusy(true);
        try {
            const r = await api.put(`/dealer/email-templates/${t.key}`, { text });
            toast.success(text.trim() ? "Vorlage gespeichert" : "Auf Standardtext zurückgesetzt");
            onSaved?.(t.key, r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setBusy(false); }
    };

    const reset = async () => {
        setText("");
        setBusy(true);
        try {
            const r = await api.put(`/dealer/email-templates/${t.key}`, { text: "" });
            toast.message("Auf Standardtext zurückgesetzt");
            onSaved?.(t.key, r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
        } finally { setBusy(false); }
    };

    return (
        <div id={`tpl-${t.key}`} className="border border-gray-200 rounded-sm" data-testid={`email-template-${t.key}`}>
            <div className="w-full flex items-center justify-between gap-3 px-4 py-3">
                <button type="button" onClick={() => setOpen((v) => !v)}
                    className="flex-1 flex items-center justify-between gap-3 text-left min-w-0"
                    data-testid={`email-template-toggle-${t.key}`}>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-sm text-[#111827] truncate">{t.label}</span>
                            {t.customized && <span className="text-[9px] font-black uppercase bg-[#FFCC00] text-[#111827] px-1.5 py-0.5 rounded-sm shrink-0">angepasst</span>}
                            {autoSend && <span className="text-[9px] font-black uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm shrink-0">Automatik</span>}
                            {!enabled && <span className="text-[9px] font-black uppercase bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-sm shrink-0">inaktiv</span>}
                        </div>
                        <p className="text-[11px] text-gray-500 mt-0.5 truncate">{t.description}</p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {/* Aktiv/Inaktiv-Schalter (außerhalb des Buttons → valides HTML) */}
                <div className="flex items-center gap-2 shrink-0">
                    <button type="button" onClick={onToggleAutoSend}
                        title={autoSend
                            ? "Automatik AN: Mails dieses Typs werden OHNE Freigabe direkt gesendet — klicken für Freigabepflicht"
                            : "Freigabepflicht: Mails dieses Typs warten in der E-Mail-Freigabe — klicken für Automatik"}
                        className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors ${autoSend ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-gray-300 text-gray-500 hover:border-emerald-400"}`}
                        data-testid={`email-template-autosend-${t.key}`}>
                        {autoSend ? "Auto" : "Freigabe"}
                    </button>
                    <button type="button" role="switch" aria-checked={enabled} onClick={toggleEnabled}
                        title={enabled ? "Automatischen Versand deaktivieren" : "Automatischen Versand aktivieren"}
                        className={`relative shrink-0 w-10 h-5 rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-gray-300"}`}
                        data-testid={`email-template-enabled-${t.key}`}>
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? "translate-x-5" : ""}`} />
                    </button>
                </div>
            </div>
            {open && (
                <div className="px-4 pb-4 space-y-2 border-t border-gray-100 pt-3">
                    <div className="flex items-center gap-3 text-[11px] text-gray-600" data-testid={`email-template-stats-${t.key}`}>
                        <span className="inline-flex items-center gap-1"><Send className="w-3 h-3 text-gray-400" /> Gesendet: <strong>{t.sent_count || 0}</strong></span>
                        <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3 text-gray-400" /> Geöffnet: <strong>{t.opened_count || 0}</strong></span>
                        {t.last_sent_at && <span className="text-gray-400">zuletzt: {new Date(t.last_sent_at).toLocaleDateString("de-DE")}</span>}
                    </div>
                    <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
                        className="text-sm" placeholder={t.default}
                        data-testid={`email-template-text-${t.key}`} />
                    {t.placeholders?.length > 0 && (
                        <p className="text-[11px] text-gray-500">
                            Platzhalter: {t.placeholders.map((p) => <code key={p} className="bg-gray-100 px-1 rounded mr-1">{p}</code>)}
                            — werden automatisch ersetzt. Leer lassen = Standardtext.
                        </p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button type="button" onClick={aiSuggest} disabled={aiBusy}
                            className="h-8 rounded-sm bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:brightness-110 text-xs font-bold"
                            data-testid={`email-template-ai-${t.key}`}>
                            {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                            {text.trim() ? "KI: überarbeiten" : "KI: Text vorschlagen"}
                        </Button>
                        <Button type="button" onClick={save} disabled={busy || !dirty}
                            className="h-8 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-xs font-bold"
                            data-testid={`email-template-save-${t.key}`}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Speichern
                        </Button>
                        <Button type="button" onClick={() => setPreview(true)} variant="outline"
                            className="h-8 rounded-sm text-xs font-bold" data-testid={`email-template-preview-${t.key}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> Vorschau &amp; Test-Mail
                        </Button>
                        {t.customized && (
                            <Button type="button" onClick={reset} disabled={busy} variant="outline"
                                className="h-8 rounded-sm text-xs font-bold" data-testid={`email-template-reset-${t.key}`}>
                                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Standard
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {preview && <PreviewModal t={t} text={text} onClose={() => setPreview(false)} />}
        </div>
    );
}

export default function EmailTemplatesSection({ currentUser }) {
    const [items, setItems] = useState(null);
    // Jul-2026 (User-Wunsch): Deep-Link ?template=<key> — Bereich öffnen,
    // die passende Vorlage aufklappen und dorthin scrollen.
    const [focusKey] = useState(() => {
        try { return new URLSearchParams(window.location.search).get("template") || ""; } catch { return ""; }
    });
    const [collapsed, setCollapsed] = useState(() => !focusKey);

    useEffect(() => {
        if (!focusKey || !items) return;
        const timer = setTimeout(() => {
            document.getElementById(`tpl-${focusKey}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 400);
        return () => clearTimeout(timer);
    }, [focusKey, items]);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/dealer/email-templates");
            setItems(r.data.items || []);
        } catch {
            setItems([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const onSaved = (key, data) => {
        setItems((arr) => (arr || []).map((it) => it.key === key
            ? { ...it, text: data.text, customized: data.customized } : it));
    };

    // Jul-2026 (User-Wunsch) — Vorlagen-Automatik zentral im Parent, damit
    // Übersicht UND Einzelzeile denselben Stand zeigen.
    const toggleAutoSend = async (t) => {
        const next = !t.auto_send;
        setItems((arr) => (arr || []).map((it) => it.key === t.key ? { ...it, auto_send: next } : it));
        try {
            await api.put(`/dealer/email-templates/${t.key}/auto-send`, { auto_send: next });
            toast.success(next
                ? `„${t.label}" wird ab jetzt AUTOMATISCH gesendet (ohne Freigabe).`
                : `„${t.label}" wartet ab jetzt wieder in der E-Mail-Freigabe.`);
        } catch (e) {
            setItems((arr) => (arr || []).map((it) => it.key === t.key ? { ...it, auto_send: !next } : it));
            toast.error(e?.response?.data?.detail || "Umschalten fehlgeschlagen");
        }
    };
    const autoCount = (items || []).filter((it) => it.auto_send).length;

    // nach Gruppen bündeln (Reihenfolge erhalten)
    const groups = [];
    (items || []).forEach((it) => {
        let g = groups.find((x) => x.name === it.group);
        if (!g) { g = { name: it.group, rows: [] }; groups.push(g); }
        g.rows.push(it);
    });

    return (
        <div className="border-b border-gray-200" data-testid="settings-email-templates">
            <div className="flex items-stretch bg-gray-50 hover:bg-gray-100 border-b border-gray-200">
            <button type="button" onClick={() => setCollapsed((v) => !v)} aria-expanded={!collapsed}
                className="flex-1 min-w-0 flex items-center gap-3 px-5 py-3 text-left" data-testid="settings-email-templates-toggle">
                <Mail className="w-4 h-4 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-[#111827]">E-Mail-Vorlagen</div>
                    <div className="text-[11px] text-gray-500">
                        Begleittext Ihrer E-Mails anpassen — Pflicht-Blöcke werden automatisch angehängt.
                    </div>
                </div>
                <span className={`text-gray-400 text-lg shrink-0 transition-transform ${collapsed ? "" : "rotate-180"}`} aria-hidden="true">⌄</span>
            </button>
            <div className="flex items-center pr-4 pl-2 shrink-0">
                <BranchOverrideToggle
                    area="email_templates"
                    currentUser={currentUser}
                    infoText="E-Mail-Vorlagen (Begleittexte & Aktiv-Status) des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                    testId="email-templates-branch-override"
                />
            </div>
            </div>
            {!collapsed && (
              <div className="px-5 pb-6 pt-4">
                {items === null ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Vorlagen werden geladen…
                    </div>
                ) : items.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4">Keine E-Mail-Typen verfügbar.</p>
                ) : (
                    <div className="space-y-5">
                        {/* Jul-2026 (User-Wunsch) — Automatik-Übersicht: alle Vorlagen
                            mit Freigabe-/Automatik-Status auf einen Blick, direkt umschaltbar. */}
                        <div className="border border-gray-200 rounded-sm" data-testid="email-templates-auto-overview">
                            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                                <span className="text-[10px] font-black uppercase tracking-wider text-gray-500">Automatik-Übersicht</span>
                                <span className="text-[10px] font-bold text-gray-400">{autoCount} von {items.length} Vorlagen senden automatisch (ohne Freigabe)</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 px-3 py-2">
                                {items.map((t) => (
                                    <div key={t.key} className="flex items-center gap-2 py-1 border-b border-gray-50 last:border-0 min-w-0">
                                        <button type="button" onClick={() => toggleAutoSend(t)}
                                            title={t.auto_send ? "Klicken für Freigabepflicht" : "Klicken für Automatik (ohne Freigabe senden)"}
                                            className={`shrink-0 w-[72px] text-center text-[9px] font-black uppercase tracking-wider px-1.5 py-1 rounded-sm border transition-colors ${t.auto_send ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-gray-300 text-gray-500 hover:border-emerald-400"}`}
                                            data-testid={`auto-overview-toggle-${t.key}`}>
                                            {t.auto_send ? "Auto" : "Freigabe"}
                                        </button>
                                        <span className="text-xs text-gray-700 truncate" title={t.label}>{t.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {groups.map((g) => (
                            <div key={g.name}>
                                <div className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">{g.name}</div>
                                <div className="space-y-2">
                                    {g.rows.map((t) => <TemplateRow key={t.key} t={t} onSaved={onSaved} initialOpen={t.key === focusKey}
                                        autoSend={!!t.auto_send} onToggleAutoSend={() => toggleAutoSend(t)} />)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
              </div>
            )}
        </div>
    );
}
